import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  Button,
  Card,
  CardContent,
  cn,
} from '@saas-pages/shared/ui'
import {
  startToolOperation,
  trackToolRejection,
} from '@saas-pages/shared/public/runtime'
import {
  clampPixel,
  colorFormats,
  keyboardPixelStep,
  loupeSampleRect,
  pointerToPixel,
  type PixelCoordinate,
  type RgbaColor,
} from '@/pages/image-color-picker/model/color'
import { MAX_PIXELS, type ImageValidationResult, validateImageFile } from '@/pages/image-color-picker/model/fileValidation'
import { BitmapSurface, releaseCanvasBackingStore } from '@/pages/image-color-picker/model/bitmapSurface'
import { decodeValidatedImage } from '@/pages/image-color-picker/model/imageLoadPipeline'
import { LatestDecodeQueue } from '@/pages/image-color-picker/model/latestDecodeQueue'
import {
  beginReplacement,
  beginReplacementOperation,
  beginSamplingOperation,
  discardSupersededOperation,
  finishProcessingFailure,
} from '@/pages/image-color-picker/model/replacementFlow'
import { LocalizedProcessingBadge, useT } from '@/shared/i18n'

interface LoadedImage {
  file: File
  width: number
  height: number
  generation: number
}

interface PointerSession {
  id: number
  generation: number
  startX: number
  startY: number
  panX: number
  panY: number
  moved: boolean
}

interface PickedColor {
  coordinate: PixelCoordinate
  color: RgbaColor
}

interface KeyboardCursor {
  coordinate: PixelCoordinate
  generation: number
}

interface QueuedImageLoad {
  validation: ImageValidationResult
  bitmap: ImageBitmap | null
}

interface PendingReplacement {
  image: LoadedImage
}

const ANALYTICS = {
  app: 'imgify',
  domain: 'imgify.ru',
  route: '/vybrat-cvet-s-kartinki',
  utility: 'image-color-picker',
  processingMode: 'local',
} as const

export function ImageColorPickerPage() {
  const t = useT()
  const copy = t.imageColorPicker
  const copyRef = useRef(copy)
  copyRef.current = copy
  // `image` is exclusively the resource whose pixels are currently committed
  // to the preview canvas. A replacement stays pending until its draw succeeds.
  const [image, setImage] = useState<LoadedImage | null>(null)
  const [pendingImage, setPendingImage] = useState<LoadedImage | null>(null)
  const [picked, setPicked] = useState<PickedColor | null>(null)
  const [hovered, setHovered] = useState<PickedColor | null>(null)
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [dropActive, setDropActive] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorGeneration, setErrorGeneration] = useState(0)
  const [copied, setCopied] = useState<string | null>(null)
  const [interactionEnabled, setInteractionEnabledState] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const loupeCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const setPreviewCanvasRef = useCallback((canvas: HTMLCanvasElement | null) => {
    if (previewCanvasRef.current && previewCanvasRef.current !== canvas) releaseCanvasBackingStore(previewCanvasRef.current)
    previewCanvasRef.current = canvas
  }, [])
  const setLoupeCanvasRef = useCallback((canvas: HTMLCanvasElement | null) => {
    if (loupeCanvasRef.current && loupeCanvasRef.current !== canvas) releaseCanvasBackingStore(loupeCanvasRef.current)
    loupeCanvasRef.current = canvas
  }, [])
  const imageResourceRef = useRef<LoadedImage | null>(null)
  const bitmapSurfaceRef = useRef(new BitmapSurface())
  const pendingReplacementRef = useRef<PendingReplacement | null>(null)
  const decodeQueueRef = useRef(new LatestDecodeQueue<QueuedImageLoad | null>())
  const operationRef = useRef<ReturnType<typeof startToolOperation> | null>(null)
  const operationGenerationRef = useRef(0)
  const operationStartedAtRef = useRef(0)
  const loadVersionRef = useRef(0)
  const reportedProcessingFailureGenerationRef = useRef(0)
  const pointerRef = useRef<PointerSession | null>(null)
  const interactionEnabledRef = useRef(false)
  const interactionGenerationRef = useRef(0)
  const mountedRef = useRef(true)
  const [keyboardCursor, setKeyboardCursor] = useState<KeyboardCursor | null>(null)
  const [focusedPixelVisible, setFocusedPixelVisible] = useState(false)
  const [cursorOverlayPosition, setCursorOverlayPosition] = useState<{ left: number; top: number } | null>(null)
  const keyboardCursorRef = useRef<KeyboardCursor | null>(null)
  const canvasContainerRef = useRef<HTMLDivElement | null>(null)

  const setLoadError = useCallback((message: string, generation: number) => {
    if (!mountedRef.current || generation !== loadVersionRef.current) return
    setError(message)
    setErrorGeneration(generation)
  }, [])

  const reportProcessingFailure = useCallback((generation: number) => {
    if (reportedProcessingFailureGenerationRef.current === generation) return
    reportedProcessingFailureGenerationRef.current = generation
    finishProcessingFailure(operationRef, operationGenerationRef, generation)
  }, [])

  const setInteractionEnabled = useCallback((enabled: boolean, generation = imageResourceRef.current?.generation ?? 0) => {
    if (enabled && generation !== imageResourceRef.current?.generation) return
    interactionEnabledRef.current = enabled
    interactionGenerationRef.current = enabled ? generation : 0
    setInteractionEnabledState(enabled)
  }, [])

  const restoreCommittedInteraction = useCallback(() => {
    pointerRef.current = null
    const committed = imageResourceRef.current
    // Rejections can happen while the next generation has disabled the
    // canvas. Restore the generation token from the committed resource
    // directly: no pending decode is allowed to keep that preview inert.
    if (committed) {
      discardSupersededOperation(operationRef, operationGenerationRef, committed.generation)
      interactionGenerationRef.current = committed.generation
      interactionEnabledRef.current = true
      setInteractionEnabledState(true)
      return
    }
    interactionGenerationRef.current = 0
    interactionEnabledRef.current = false
    setInteractionEnabledState(false)
  }, [])

  const failPixelProcessing = useCallback((generation: number) => {
    const committed = imageResourceRef.current
    if (
      committed?.generation !== generation
      || interactionGenerationRef.current !== generation
    ) return
    // A hover can fail after a previous click has already completed the
    // replacement-owned operation. Open a generation-owned operation lazily
    // here so this failed sampling attempt still has one canonical terminal
    // outcome without creating unfinished analytics operations for successful
    // pointer moves.
    beginSamplingOperation(
      operationRef,
      operationGenerationRef,
      generation,
      () => startToolOperation(ANALYTICS),
    )
    pointerRef.current = null
    setHovered(null)
    releaseCanvasBackingStore(loupeCanvasRef.current)
    setInteractionEnabled(false)
    if (mountedRef.current && imageResourceRef.current?.generation === generation) {
      setError(copyRef.current.errors.canvas)
      setErrorGeneration(generation)
    }
    reportProcessingFailure(generation)
  }, [reportProcessingFailure, setInteractionEnabled])

  const discardPendingReplacement = useCallback(() => {
    pendingReplacementRef.current = null
    bitmapSurfaceRef.current.dispose()
  }, [])

  const releaseCanvases = useCallback(() => {
    releaseCanvasBackingStore(previewCanvasRef.current)
    releaseCanvasBackingStore(loupeCanvasRef.current)
  }, [])

  const clearKeyboardCursor = useCallback(() => {
    keyboardCursorRef.current = null
    setKeyboardCursor(null)
    setFocusedPixelVisible(false)
    setCursorOverlayPosition(null)
  }, [])

  // Layout-effect cleanup runs in React's layout teardown phase, while the
  // canvas nodes are still attached. A passive effect runs too late: its refs
  // may already be detached and retain a 16 MP backing store until GC.
  useLayoutEffect(() => () => {
    releaseCanvases()
  }, [releaseCanvases])

  const reset = useCallback(() => {
    loadVersionRef.current += 1
    decodeQueueRef.current.cancelPending()
    discardPendingReplacement()
    pointerRef.current = null
    operationRef.current = null
    operationGenerationRef.current = 0
    setInteractionEnabled(false)
    operationStartedAtRef.current = 0
    const previous = imageResourceRef.current
    if (previous) {
      imageResourceRef.current = null
    }
    bitmapSurfaceRef.current.dispose()
    releaseCanvases()
    setImage(null)
    setPendingImage(null)
    setPicked(null)
    setHovered(null)
    clearKeyboardCursor()
    setZoom(1)
    setPan({ x: 0, y: 0 })
    setError(null)
    setErrorGeneration(0)
    setLoading(false)
    setCopied(null)
  }, [clearKeyboardCursor, discardPendingReplacement, releaseCanvases, setInteractionEnabled])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      loadVersionRef.current += 1
      decodeQueueRef.current.cancelPending()
      discardPendingReplacement()
      pointerRef.current = null
      keyboardCursorRef.current = null
      operationRef.current = null
      operationGenerationRef.current = 0
      setInteractionEnabled(false)
      const current = imageResourceRef.current
      if (current) {
        imageResourceRef.current = null
      }
      bitmapSurfaceRef.current.dispose()
    }
  }, [discardPendingReplacement, setInteractionEnabled])

  useEffect(() => {
    if (!pendingImage || pendingImage.generation !== loadVersionRef.current || !previewCanvasRef.current) return
    const pending = pendingReplacementRef.current
    if (!pending || pending.image.generation !== pendingImage.generation) return
    const canvas = previewCanvasRef.current
    if (!bitmapSurfaceRef.current.draw(canvas, pendingImage.width, pendingImage.height, {
      preserveCommittedPreview: imageResourceRef.current !== null,
      shouldCommit: () => (
        pendingImage.generation === loadVersionRef.current
        && pendingReplacementRef.current?.image.generation === pendingImage.generation
      ),
    })) {
      pendingReplacementRef.current = null
      setPendingImage(null)
      setLoadError(copyRef.current.errors.canvas, pendingImage.generation)
      reportProcessingFailure(pendingImage.generation)
      restoreCommittedInteraction()
      return
    }
    if (operationGenerationRef.current !== pendingImage.generation) return
    pendingReplacementRef.current = null
    imageResourceRef.current = pendingImage
    const centeredCursor = {
      coordinate: clampPixel(
        Math.floor(pendingImage.width / 2),
        Math.floor(pendingImage.height / 2),
        pendingImage.width,
        pendingImage.height,
      ),
      generation: pendingImage.generation,
    }
    keyboardCursorRef.current = centeredCursor
    setKeyboardCursor(centeredCursor)
    setFocusedPixelVisible(document.activeElement === canvas)
    setImage(pendingImage)
    setPendingImage(null)
    setInteractionEnabled(true, pendingImage.generation)
  }, [pendingImage, reportProcessingFailure, restoreCommittedInteraction, setInteractionEnabled, setLoadError])

  const loadFile = useCallback(async (file: File) => {
    const version = beginReplacement(loadVersionRef)
    decodeQueueRef.current.cancelPending()
    discardPendingReplacement()
    pointerRef.current = null
    setError(null)
    setInteractionEnabled(false)
    setFocusedPixelVisible(false)
    setCursorOverlayPosition(null)
    setPicked(null)
    setHovered(null)
    setCopied(null)
    setLoading(true)
    let bitmap: ImageBitmap | null = null
    let failureMessage = copy.errors.corrupt
    try {
      const queuedLoad = await decodeQueueRef.current.enqueue({
        generation: version,
        decode: async () => {
          return decodeValidatedImage({
            validate: () => validateImageFile(file),
            isRejected: (validation) => validation.error !== null,
            onValidationSuccess: () => {
              if (!beginReplacementOperation(
                operationRef,
                operationGenerationRef,
                loadVersionRef,
                version,
                () => startToolOperation(ANALYTICS),
              )) return
              operationStartedAtRef.current = performance.now()
            },
            decode: () => createImageBitmap(file),
            isCurrent: () => mountedRef.current && version === loadVersionRef.current,
          }
          )
        },
      })
      if (!queuedLoad || version !== loadVersionRef.current) {
        queuedLoad?.bitmap?.close()
        return
      }
      const { validation } = queuedLoad
      if (validation.error) {
        const rejection = validation.error === 'unsupported'
          ? { message: copy.errors.unsupported, analytics: 'unsupported_input' as const }
          : validation.error === 'too_many_pixels'
            ? { message: copy.errors.tooManyPixels, analytics: 'size_limit' as const }
            : validation.error === 'too_large'
              ? { message: copy.errors.tooLarge, analytics: 'size_limit' as const }
              : { message: copy.errors.corrupt, analytics: 'invalid_input' as const }
        setLoadError(rejection.message, version)
        trackToolRejection(ANALYTICS, rejection.analytics)
        setPendingImage(null)
        restoreCommittedInteraction()
        return
      }
      bitmap = queuedLoad.bitmap
      if (!bitmap) throw new Error(copy.errors.corrupt)
      if (bitmap.width < 1 || bitmap.height < 1) {
        throw new Error(copy.errors.corrupt)
      }
      if (bitmap.width * bitmap.height > MAX_PIXELS) {
        failureMessage = copy.errors.tooManyPixels
        throw new Error(failureMessage)
      }
      if (version !== loadVersionRef.current) {
        bitmap.close()
        return
      }

      const width = bitmap.width
      const height = bitmap.height
      bitmapSurfaceRef.current.setPending(bitmap)
      bitmap = null
      const nextImage = { file, width, height, generation: version }
      if (version !== loadVersionRef.current) {
        bitmapSurfaceRef.current.dispose()
        return
      }
      pendingReplacementRef.current = { image: nextImage }
      setPendingImage(nextImage)
      setPicked(null)
      setHovered(null)
      setZoom(1)
      setPan({ x: 0, y: 0 })
    } catch {
      bitmap?.close()
      if (version !== loadVersionRef.current) return
      setPendingImage(null)
      setLoadError(failureMessage, version)
      reportProcessingFailure(version)
      restoreCommittedInteraction()
    } finally {
      if (version === loadVersionRef.current) setLoading(false)
    }
  }, [copy.errors, discardPendingReplacement, reportProcessingFailure, restoreCommittedInteraction, setInteractionEnabled, setLoadError])

  const addFiles = useCallback((files: FileList | File[]) => {
    const [file] = Array.from(files)
    if (file) void loadFile(file)
  }, [loadFile])

  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      const file = Array.from(event.clipboardData?.files ?? []).find((item) => item.type.startsWith('image/'))
      if (file) {
        event.preventDefault()
        void loadFile(file)
      }
    }
    window.addEventListener('paste', handlePaste)
    return () => window.removeEventListener('paste', handlePaste)
  }, [loadFile])

  const pixelCoordinateAt = useCallback((clientX: number, clientY: number): PixelCoordinate | null => {
    const preview = previewCanvasRef.current
    const committed = imageResourceRef.current
    if (!preview || !committed || interactionGenerationRef.current !== committed.generation) return null
    return pointerToPixel(clientX, clientY, preview.getBoundingClientRect(), committed.width, committed.height)
  }, [])

  const readPixel = useCallback((coordinate: PixelCoordinate): PickedColor | null => {
    const preview = previewCanvasRef.current
    const committed = imageResourceRef.current
    if (!preview || !committed || interactionGenerationRef.current !== committed.generation) return null
    try {
      const context = preview.getContext('2d', { willReadFrequently: true })
      if (!context) throw new Error('Preview context is unavailable')
      const pixel = context.getImageData(coordinate.x, coordinate.y, 1, 1).data
      return {
        coordinate,
        color: { r: pixel[0] ?? 0, g: pixel[1] ?? 0, b: pixel[2] ?? 0, a: pixel[3] ?? 0 },
      }
    } catch {
      failPixelProcessing(committed.generation)
      return null
    }
  }, [failPixelProcessing])

  const updateLoupe = useCallback((next: PickedColor | null): boolean => {
    const loupe = loupeCanvasRef.current
    const preview = previewCanvasRef.current
    if (!loupe || !preview || !next || !image) return true
    try {
      const size = 11
      loupe.width = size
      loupe.height = size
      const context = loupe.getContext('2d')
      if (!context) throw new Error('Loupe context is unavailable')
      context.imageSmoothingEnabled = false
      context.clearRect(0, 0, size, size)
      const sample = loupeSampleRect(next.coordinate, image.width, image.height, size)
      if (sample.sourceWidth < 1 || sample.sourceHeight < 1) return true
      context.drawImage(
        preview,
        sample.sourceX,
        sample.sourceY,
        sample.sourceWidth,
        sample.sourceHeight,
        sample.destinationX,
        sample.destinationY,
        sample.sourceWidth,
        sample.sourceHeight,
      )
      return true
    } catch {
      failPixelProcessing(image.generation)
      return false
    }
  }, [failPixelProcessing, image])

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!interactionEnabledRef.current || interactionGenerationRef.current !== imageResourceRef.current?.generation) return
    const session = pointerRef.current
    if (session && session.generation !== imageResourceRef.current?.generation) {
      pointerRef.current = null
      return
    }
    if (session?.id === event.pointerId) {
      const dx = event.clientX - session.startX
      const dy = event.clientY - session.startY
      if (session.moved || Math.hypot(dx, dy) > 4) {
        session.moved = true
        setPan({ x: session.panX + dx, y: session.panY + dy })
        return
      }
    }
    const coordinate = pixelCoordinateAt(event.clientX, event.clientY)
    const next = coordinate ? readPixel(coordinate) : null
    setHovered(next)
    if (!updateLoupe(next)) setHovered(null)
  }, [pixelCoordinateAt, readPixel, updateLoupe])

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!interactionEnabledRef.current || interactionGenerationRef.current !== imageResourceRef.current?.generation) return
    event.currentTarget.setPointerCapture(event.pointerId)
    pointerRef.current = {
      id: event.pointerId,
      generation: imageResourceRef.current?.generation ?? 0,
      startX: event.clientX,
      startY: event.clientY,
      panX: pan.x,
      panY: pan.y,
      moved: false,
    }
  }, [pan])

  const handlePointerUp = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    const session = pointerRef.current
    if (!session || session.id !== event.pointerId) return
    pointerRef.current = null
    if (
      !interactionEnabledRef.current
      || interactionGenerationRef.current !== imageResourceRef.current?.generation
      || session.generation !== imageResourceRef.current?.generation
      || session.moved
    ) return
    const coordinate = pixelCoordinateAt(event.clientX, event.clientY)
    if (!coordinate) return
    // The first sample settles the active replacement operation. Its duration
    // must therefore keep the validation/decode start time, rather than being
    // reset to the pointer event time. Later samples create their own operation.
    const startsSamplingOperation = operationRef.current === null
    if (!beginSamplingOperation(
      operationRef,
      operationGenerationRef,
      session.generation,
      () => startToolOperation(ANALYTICS),
    )) return
    if (startsSamplingOperation) operationStartedAtRef.current = performance.now()
    const next = readPixel(coordinate)
    if (!next) return
    if (!updateLoupe(next)) return
    setPicked(next)
    setHovered(next)
    operationRef.current?.success(performance.now() - operationStartedAtRef.current)
    operationRef.current = null
    operationGenerationRef.current = 0
  }, [pixelCoordinateAt, readPixel, updateLoupe])

  const updateCursorOverlayPosition = useCallback(() => {
    const canvas = previewCanvasRef.current
    const container = canvasContainerRef.current
    const cursor = keyboardCursorRef.current
    const committed = imageResourceRef.current
    if (!canvas || !container || !cursor || !committed || cursor.generation !== committed.generation) {
      setCursorOverlayPosition(null)
      return
    }
    const canvasBounds = canvas.getBoundingClientRect()
    const containerBounds = container.getBoundingClientRect()
    const next = {
      left: canvasBounds.left - containerBounds.left - container.clientLeft
        + ((cursor.coordinate.x + 0.5) / committed.width) * canvasBounds.width,
      top: canvasBounds.top - containerBounds.top - container.clientTop
        + ((cursor.coordinate.y + 0.5) / committed.height) * canvasBounds.height,
    }
    setCursorOverlayPosition((current) => (
      current && Math.abs(current.left - next.left) < 0.01 && Math.abs(current.top - next.top) < 0.01
        ? current
        : next
    ))
  }, [])

  useLayoutEffect(() => {
    if (!focusedPixelVisible || !keyboardCursor || !image) {
      setCursorOverlayPosition(null)
      return
    }
    updateCursorOverlayPosition()
    const observer = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(updateCursorOverlayPosition)
    if (previewCanvasRef.current) observer?.observe(previewCanvasRef.current)
    if (canvasContainerRef.current) observer?.observe(canvasContainerRef.current)
    window.addEventListener('resize', updateCursorOverlayPosition)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', updateCursorOverlayPosition)
    }
  }, [focusedPixelVisible, image, keyboardCursor, pan, updateCursorOverlayPosition, zoom])

  const handleCanvasKeyDown = useCallback((event: React.KeyboardEvent<HTMLCanvasElement>) => {
    const committed = imageResourceRef.current
    if (
      !committed
      || !interactionEnabledRef.current
      || interactionGenerationRef.current !== committed.generation
    ) return
    let dx = 0
    let dy = 0
    const step = event.shiftKey ? 10 : 1
    switch (event.key) {
      case 'ArrowUp': dy = -step; break
      case 'ArrowDown': dy = step; break
      case 'ArrowLeft': dx = -step; break
      case 'ArrowRight': dx = step; break
      case 'Home':
        dx = -committed.width
        dy = -committed.height
        break
      case 'End':
        dx = committed.width
        dy = committed.height
        break
      case 'Enter':
      case ' ':
        event.preventDefault()
        const storedCursor = keyboardCursorRef.current
        const current = clampPixel(
          storedCursor?.generation === committed.generation
            ? storedCursor.coordinate.x
            : Math.floor(committed.width / 2),
          storedCursor?.generation === committed.generation
            ? storedCursor.coordinate.y
            : Math.floor(committed.height / 2),
          committed.width,
          committed.height,
        )
        const currentCursor = { coordinate: current, generation: committed.generation }
        keyboardCursorRef.current = currentCursor
        setKeyboardCursor(currentCursor)
        const startsOp = operationRef.current === null
        if (!beginSamplingOperation(
          operationRef,
          operationGenerationRef,
          committed.generation,
          () => startToolOperation(ANALYTICS),
        )) return
        if (startsOp) operationStartedAtRef.current = performance.now()
        const next = readPixel(current)
        if (!next) return
        if (!updateLoupe(next)) return
        setPicked(next)
        setHovered(next)
        operationRef.current?.success(performance.now() - operationStartedAtRef.current)
        operationRef.current = null
        operationGenerationRef.current = 0
        return
      default:
        return
    }
    event.preventDefault()
    const storedCursor = keyboardCursorRef.current
    const pixel = storedCursor?.generation === committed.generation
      ? storedCursor.coordinate
      : clampPixel(
        Math.floor(committed.width / 2),
        Math.floor(committed.height / 2),
        committed.width,
        committed.height,
      )
    const next = keyboardPixelStep(pixel, dx, dy, committed.width, committed.height)
    const nextCursor = { coordinate: next, generation: committed.generation }
    keyboardCursorRef.current = nextCursor
    setKeyboardCursor(nextCursor)
    setFocusedPixelVisible(true)
    const hoverResult = readPixel(next)
    setHovered(hoverResult)
    if (!updateLoupe(hoverResult)) setHovered(null)
  }, [readPixel, updateLoupe])

  const copyValue = useCallback(async (label: string, value: string) => {
    const version = loadVersionRef.current
    try {
      await navigator.clipboard.writeText(value)
      if (!mountedRef.current || version !== loadVersionRef.current) return
      setCopied(label)
    } catch {
      if (!mountedRef.current || version !== loadVersionRef.current) return
      setError(copy.errors.copy)
    }
  }, [copy.errors.copy])

  const formats = picked ? colorFormats(picked.color) : null
  const displayedImage = image ?? pendingImage
  const activeKeyboardPixel = keyboardCursor && image && keyboardCursor.generation === image.generation
    ? keyboardCursor.coordinate
    : null
  const cursorAnnouncement = activeKeyboardPixel
    ? copy.pixelAnnouncement
      .replace('{x}', String(activeKeyboardPixel.x + 1))
      .replace('{y}', String(activeKeyboardPixel.y + 1))
    : ''
  const selectedAnnouncement = picked && formats
    ? copy.selectedAnnouncement.replace('{color}', formats.hex)
    : ''

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <header className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold uppercase tracking-[0.18em] text-primary">{copy.eyebrow}</span>
          <LocalizedProcessingBadge variant="local" />
        </div>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{copy.title}</h1>
        <p className="max-w-3xl text-muted-foreground">{copy.description}</p>
      </header>

      <input
        ref={inputRef}
        id="image-color-picker-input"
        className="sr-only"
        type="file"
        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
        aria-label={copy.dropTitle}
        onChange={(event) => {
          if (event.target.files) addFiles(event.target.files)
          event.target.value = ''
        }}
      />

      {!displayedImage ? (
        <Card>
          <CardContent className="p-5 sm:p-8">
            <button
              type="button"
              className={cn('flex min-h-64 w-full flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed p-8 text-center transition', dropActive ? 'border-primary bg-primary/10' : 'border-border bg-muted/30 hover:border-primary/60')}
              onClick={() => inputRef.current?.click()}
              onDragOver={(event) => { event.preventDefault(); setDropActive(true) }}
              onDragLeave={() => setDropActive(false)}
              onDrop={(event) => { event.preventDefault(); setDropActive(false); addFiles(event.dataTransfer.files) }}
            >
              <span className="text-xl font-semibold">{loading ? copy.loading : copy.dropTitle}</span>
              <span className="max-w-xl text-sm text-muted-foreground">{copy.dropHint}</span>
              <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium">JPG · PNG · WebP · 50 MB · 16 MP</span>
            </button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <Card className="overflow-hidden">
            <CardContent className="space-y-4 p-4 sm:p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-muted-foreground">{displayedImage.file.name} · {displayedImage.width}×{displayedImage.height}</div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" aria-label={copy.zoomOut} onClick={() => setZoom((value) => Math.max(0.25, value - 0.25))}>−</Button>
                  <span className="min-w-16 text-center text-sm font-medium">{Math.round(zoom * 100)}%</span>
                  <Button variant="outline" size="sm" aria-label={copy.zoomIn} onClick={() => setZoom((value) => Math.min(8, value + 0.25))}>+</Button>
                  <Button variant="outline" size="sm" aria-label={copy.fit} onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }) }}>{copy.fit}</Button>
                  <Button variant="outline" size="sm" aria-label={copy.reset} onClick={() => inputRef.current?.click()}>{copy.reset}</Button>
                </div>
              </div>
              <div ref={canvasContainerRef} style={{ position: 'relative' }} className="relative min-h-[15rem] max-h-[70vh] touch-none overflow-hidden rounded-xl border bg-[linear-gradient(45deg,hsl(var(--muted))_25%,transparent_25%),linear-gradient(-45deg,hsl(var(--muted))_25%,transparent_25%),linear-gradient(45deg,transparent_75%,hsl(var(--muted))_75%),linear-gradient(-45deg,transparent_75%,hsl(var(--muted))_75%)] bg-[length:20px_20px] bg-[position:0_0,0_10px,10px_-10px,-10px_0px] lg:h-[30rem]">
                <canvas
                  ref={setPreviewCanvasRef}
                  className="max-h-full max-w-full cursor-crosshair select-none object-contain"
                  style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: 'top left', pointerEvents: interactionEnabled ? 'auto' : 'none' }}
                  tabIndex={interactionEnabled ? 0 : -1}
                  role="application"
                  aria-label={copy.canvasLabel}
                  aria-description={copy.canvasKeyboardHint}
                  data-testid="image-color-picker-preview"
                  data-keyboard-generation={keyboardCursor && image && keyboardCursor.generation === image.generation ? keyboardCursor.generation : undefined}
                  data-keyboard-x={activeKeyboardPixel?.x}
                  data-keyboard-y={activeKeyboardPixel?.y}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={() => { pointerRef.current = null }}
                  onKeyDown={handleCanvasKeyDown}
                  onFocus={() => {
                    const committed = imageResourceRef.current
                    if (!committed) return
                    const storedCursor = keyboardCursorRef.current
                    const coordinate = clampPixel(
                      storedCursor?.generation === committed.generation
                        ? storedCursor.coordinate.x
                        : Math.floor(committed.width / 2),
                      storedCursor?.generation === committed.generation
                        ? storedCursor.coordinate.y
                        : Math.floor(committed.height / 2),
                      committed.width,
                      committed.height,
                    )
                    const nextCursor = { coordinate, generation: committed.generation }
                    keyboardCursorRef.current = nextCursor
                    setKeyboardCursor(nextCursor)
                    setFocusedPixelVisible(true)
                  }}
                  onBlur={() => {
                    setFocusedPixelVisible(false)
                  }}
                />
                {focusedPixelVisible && activeKeyboardPixel && cursorOverlayPosition && (
                  <div
                    data-testid="image-color-picker-keyboard-cursor"
                    data-keyboard-x={activeKeyboardPixel.x}
                    data-keyboard-y={activeKeyboardPixel.y}
                    className="pointer-events-none absolute z-10 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.5),0_0_4px_rgba(0,0,0,0.3)]"
                    style={{
                      position: 'absolute',
                      left: cursorOverlayPosition.left,
                      top: cursorOverlayPosition.top,
                      width: 12,
                      height: 12,
                      transform: 'translate(-50%, -50%)',
                    }}
                    aria-hidden="true"
                  />
                )}
                <div data-testid="image-color-picker-announcement" aria-live="polite" aria-atomic="true" className="sr-only">
                  {focusedPixelVisible ? cursorAnnouncement : ''}
                  {selectedAnnouncement}
                </div>
                <div className={cn('pointer-events-none absolute bottom-3 left-3 overflow-hidden rounded-lg border-2 border-background bg-background shadow-xl', hovered ? 'visible' : 'invisible')}>
                  <canvas ref={setLoupeCanvasRef} className="h-28 w-28 [image-rendering:pixelated]" />
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage: 'linear-gradient(to right, rgb(255 255 255 / 90%) 1px, transparent 1px), linear-gradient(to bottom, rgb(255 255 255 / 90%) 1px, transparent 1px), linear-gradient(to right, rgb(0 0 0 / 75%) 2px, transparent 2px), linear-gradient(to bottom, rgb(0 0 0 / 75%) 2px, transparent 2px)',
                      backgroundSize: 'calc(100% / 11) calc(100% / 11), calc(100% / 11) calc(100% / 11), calc(100% / 11) calc(100% / 11), calc(100% / 11) calc(100% / 11)',
                    }}
                  />
                  <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-white shadow-[0_0_0_1px_rgb(0_0_0),0_0_3px_rgb(0_0_0)]" />
                  <div className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-white shadow-[0_0_0_1px_rgb(0_0_0),0_0_3px_rgb(0_0_0)]" />
                </div>
              </div>
              <p className="text-sm text-muted-foreground">{copy.canvasHint}</p>
            </CardContent>
          </Card>

          <Card className="space-y-3 divide-y p-0 sm:space-y-5 sm:p-5">
            <div className="flex items-center gap-3 p-4 sm:p-0">
              <div className="h-12 w-12 shrink-0 rounded-xl border shadow-inner sm:h-16 sm:w-16" style={{ backgroundColor: picked ? `rgba(${picked.color.r}, ${picked.color.g}, ${picked.color.b}, ${picked.color.a / 255})` : 'transparent' }} />
              <div>
                <h2 className="font-semibold">{copy.resultTitle}</h2>
                <p className="text-sm text-muted-foreground">{picked ? `x: ${picked.coordinate.x}, y: ${picked.coordinate.y}` : copy.noSelection}</p>
              </div>
            </div>
            <div className="p-4 pt-3 sm:p-0 sm:pt-0">
              {formats && (
                <>
                  {formats.transparent && <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">{copy.transparent}</p>}
                  {!formats.transparent && formats.alpha < 1 && <p className="text-sm text-muted-foreground">Alpha: {Math.round(formats.alpha * 100)}%</p>}
                  {Object.entries({ HEX: formats.hex, RGB: formats.rgb, HSL: formats.hsl, HSV: formats.hsv }).map(([label, value]) => (
                    <button key={label} type="button" className="block w-full rounded-lg border p-3 text-left transition hover:border-primary" onClick={() => void copyValue(label, value)}>
                      <span className="block text-xs font-semibold text-muted-foreground">{label}</span>
                      <code className="break-all text-sm">{value}</code>
                      {copied === label && <span className="ml-2 text-xs text-primary">{copy.copied}</span>}
                    </button>
                  ))}
                </>
              )}
              <Button data-testid="image-color-picker-reset" variant="outline" className="w-full" onClick={reset}>{copy.reset}</Button>
            </div>
          </Card>
        </div>
      )}

      {error && <p role="alert" data-load-generation={errorGeneration || undefined} className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">{error}</p>}

      <section className="grid gap-4 md:grid-cols-3">
        {copy.howTo.map((step, index) => <Card key={step}><CardContent className="space-y-2 p-5"><span className="text-sm font-semibold text-primary">{index + 1}</span><p>{step}</p></CardContent></Card>)}
      </section>
      <section className="space-y-4"><h2 className="text-2xl font-bold">{copy.faqTitle}</h2>{copy.faq.map((item) => <Card key={item.question}><CardContent className="space-y-2 p-5"><h3 className="font-semibold">{item.question}</h3><p className="text-sm text-muted-foreground">{item.answer}</p></CardContent></Card>)}</section>
      <section className="space-y-4"><h2 className="text-2xl font-bold">{copy.relatedTitle}</h2><div className="flex flex-wrap gap-3"><Button asChild variant="outline"><a href="/obrezat-foto">{copy.relatedCrop}</a></Button><Button asChild variant="outline"><a href="/izmenit-razmer-foto">{copy.relatedResize}</a></Button><Button asChild variant="outline"><a href="/szhat-izobrazhenie">{copy.relatedCompress}</a></Button></div></section>
    </div>
  )
}
